import React, { PropsWithChildren, ReactNode, useState, useCallback } from "react";
import {
    Accordion,
    FieldCheckbox,
    OnboardingAside,
    OnboardingContent,
    PrimaryAlternateControl,
} from "@xapp/components";

import classes from "./QuestionStep.module.scss";

export interface QuestionStepProps<T> {
    title: string;
    description?: ReactNode;
    style?: React.CSSProperties;
    className?: string;
    aside?: ReactNode;

    hasValueLabel: string;
    hasValueDescription?: ReactNode | string;
    noValueLabel: string;
    noValueDescription?: ReactNode | string;
    /**
     * Label for the value input field.
     * If not provided, the input field will not be rendered.
     *
     * @deprecated Not really used anymore.
     */
    valueLabel?: string;
    /**
     * Initial value for the input field.
     */
    initialValue?: T;

    /**
     * Component to render the input field.
     * Receives current value, a setValue function, and disabled boolean.
     */
    renderInput?: (value: T, setValue: (val: T) => void, disabled: boolean) => ReactNode;

    /**
     * Determines whether the next button can be clicked.
     */
    canClickNext: (hasValue: boolean | undefined, value: T) => boolean;

    onNext: (hasValue: boolean, value?: T) => void;
    onBack?: () => void;
}

export const QuestionStep = <T,>({
    title,
    description,
    style,
    className,
    aside,
    hasValueLabel,
    hasValueDescription,
    noValueLabel,
    noValueDescription,
    valueLabel,
    initialValue,
    renderInput,
    canClickNext,
    onNext,
    onBack,
}: PropsWithChildren<QuestionStepProps<T>>) => {
    const [value, setValue] = useState<T>(initialValue as T);

    const [hasValue, setHasValue] = useState<boolean>();

    const noValue = typeof hasValue === "boolean" ? !hasValue : false;

    const handleNextClick = () => {
        const returnValue = hasValue ? value : undefined;
        onNext(!!hasValue, returnValue);
    };

    const handleHasValueChange = useCallback((val: boolean) => setHasValue(val), []);
    const handleNoValueChange = useCallback((val: boolean) => setHasValue(!val), []);

    const canProceed = canClickNext(hasValue, value);

    return (
        <>
            {aside && <OnboardingAside>{aside}</OnboardingAside>}
            <OnboardingContent style={style} className={className}>
                <h2>{title}</h2>

                {description && (
                    <div className={classes.descriptionWrapper}>
                        <p>{description}</p>
                    </div>
                )}
                <div className={classes.accordionWrapper}>
                    <Accordion
                        title={
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <FieldCheckbox
                                    fieldStyle={{ marginBottom: "2px" }}
                                    onChange={handleHasValueChange}
                                    value={!!hasValue}
                                />
                                <h3>{hasValueLabel}</h3>
                            </div>
                        }
                        onChange={(e, expanded) => handleHasValueChange(expanded)}
                        disableExpandIcon={true}
                        expanded={!!hasValue}
                    >
                        {hasValueDescription && (
                            <div style={{ paddingBottom: "20px" }}>
                                <p>{hasValueDescription}</p>
                            </div>
                        )}
                        {typeof renderInput === "function" && (
                            <div style={{ paddingBottom: "20px" }}>
                                {valueLabel && <p>{`Great! Please enter your ${valueLabel} below...`}</p>}
                                {renderInput(value, setValue, !hasValue)}
                            </div>
                        )}
                    </Accordion>

                    <Accordion
                        title={
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <FieldCheckbox
                                    fieldStyle={{ marginBottom: "2px" }}
                                    onChange={handleNoValueChange}
                                    value={noValue}
                                />
                                <h3>{noValueLabel}</h3>
                            </div>
                        }
                        onChange={(e, expanded) => handleNoValueChange(expanded)}
                        disableExpandIcon={true}
                        expanded={noValue}
                    >
                        {noValueDescription && (
                            <div style={{ paddingBottom: "20px" }}>
                                <p>{noValueDescription}</p>
                            </div>
                        )}
                    </Accordion>
                </div>
                <PrimaryAlternateControl
                    labels={{
                        primary: "Next",
                    }}
                    onPrimary={handleNextClick}
                    disabled={{
                        primary: !canProceed,
                        alternate: false,
                    }}
                />
            </OnboardingContent>
        </>
    );
};
